import { Pipe, PipeTransform, OnDestroy } from '@angular/core';
import { Observable, combineLatest, interval, map, startWith } from 'rxjs';
import { TranslationService } from 'src/app/services/utilities/translation.service';

@Pipe({
  name: 'timeAgo'
})
export class TimeAgoPipe implements PipeTransform, OnDestroy {

  private timer$ = interval(1000 * 30).pipe(startWith(0)); // every 30s, adjust for performance
  private subscription: any;

  constructor(private translationService: TranslationService) {}

  transform(timestamp: string | Date): Observable<string> {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return this.translationService.languageChanges().pipe(
        map(() => this.translationService.t('Invalid date'))
      );
    }

    // Combine language changes + timer ticks
    return combineLatest([this.translationService.languageChanges(), this.timer$]).pipe(
      map(([lang]) => this.formatTimeAgo(date, lang))
    );
  }

  private formatTimeAgo(date: Date, lang: string): string {
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return this.translateString(1, 'second', lang);
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return this.translateString(minutes, 'minute', lang);
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return this.translateString(hours, 'hour', lang);
    const days = Math.floor(hours / 24);
    if (days < 30) return this.translateString(days, 'day', lang);
    const months = Math.floor(days / 30);
    if (months < 12) return this.translateString(months, 'month', lang);
    const years = Math.floor(months / 12);
    return this.translateString(years, 'year', lang);
  }

  private translateString(value: number, unit: string, lang: string): string {
    if (lang === 'fr') {
      const frUnits: any = {
        second: 'seconde',
        minute: 'minute',
        hour: 'heure',
        day: 'jour',
        month: 'mois',
        year: 'an'
      };
      const plural = value > 1 && unit !== 'month' && unit !== 'year' ? 's' : '';
      return `il y a ${value} ${frUnits[unit]}${plural}`;
    }
    // English default
    const plural = value > 1 ? 's' : '';
    return `${value} ${unit}${plural} ago`;
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
  }
}
